import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Navigate, Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { useEffect } from "react";

import { AccessRequests } from "@/features/admin/AccessRequests";
import { AppShell } from "@/layouts/AppShell";
import { ClientList } from "@/features/clients/ClientList";
import { ClientProfile } from "@/features/clients/ClientProfile";
import { EmployeeList } from "@/features/employees/EmployeeList";
import { FunnelPage } from "@/features/analytics/FunnelPage";
import { TargetsPage } from "@/features/analytics/TargetsPage";
import { ActivityDashboard } from "@/features/analytics/ActivityDashboard";
import { BehaviourPage } from "@/features/analytics/BehaviourPage";
import { EmployeeSegments } from "@/features/employees/EmployeeSegments";
import { TeamPerformance } from "@/features/analytics/TeamPerformance";
import { EmployeeProfile } from "@/features/employees/EmployeeProfile";
import { ExecutiveDashboard } from "@/features/executive/ExecutiveDashboard";
import { InsightFeed } from "@/features/intelligence/InsightFeed";
import { PerformanceComparison } from "@/features/analytics/PerformanceComparison";
import { Login } from "@/features/executive/Login";
import { QualityScorecard } from "@/features/quality/QualityScorecard";
import { Register } from "@/features/auth/Register";
import { SystemHealth } from "@/features/quality/SystemHealth";
import { UserManagement } from "@/features/admin/UserManagement";
import { useAuthStore } from "@/store/auth";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // staleTime is matched to the backend cache TTL, so polling does not
      // hammer endpoints whose data cannot have changed yet.
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // 403 is an authorisation answer, not a transient failure.
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 403 || status === 404) return false;
        return failureCount < 2;
      },
    },
  },
});

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const loadUser = useAuthStore((s) => s.loadUser);

  useEffect(() => {
    if (token && !user) void loadUser();
  }, [token, user, loadUser]);

  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * Guards the two administration screens.
 *
 * The API refuses a non-admin regardless, so this is not the control — it stops
 * someone who typed the URL from staring at a page of 403s and concluding the
 * app is broken.
 */
function RequireAdmin({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  if (!user) return null;            // still loading /profile/me
  if (user.role !== "ADMIN") return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route element={<RequireAuth><AppShell /></RequireAuth>}>
            <Route index element={<ExecutiveDashboard />} />
            <Route path="opportunities" element={<InsightFeed kind="opportunity" />} />
            <Route path="risks" element={<InsightFeed kind="risk" />} />
            <Route path="funnel" element={<FunnelPage />} />
            <Route path="vps" element={<TeamPerformance />} />
            <Route path="performance" element={<PerformanceComparison />} />
            <Route path="behaviour" element={<BehaviourPage />} />
            <Route path="activities" element={<ActivityDashboard />} />
            <Route path="employee-kpis" element={<EmployeeSegments />} />
            <Route path="clients" element={<ClientList />} />
            <Route path="clients/:clientId" element={<ClientProfile />} />
            <Route path="employees" element={<EmployeeList />} />
            <Route path="employees/:personKey" element={<EmployeeProfile />} />
            <Route path="targets" element={<TargetsPage />} />
            <Route path="quality" element={<QualityScorecard />} />
            <Route path="health" element={<SystemHealth />} />
            <Route path="users" element={<RequireAdmin><UserManagement /></RequireAdmin>} />
            <Route path="access-requests"
                   element={<RequireAdmin><AccessRequests /></RequireAdmin>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}
